import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';
import { IMAGE_PATH } from 'common/ImageConstants/ImagePathUtils';
import DELIVERY_ADDRESS_SEARCH_FORM_CONSTANTS from '../../organisms/DeliveryAddressSearchForm/Constants/DeliveryAddressSearchFormConstants';
const { SearchIconGrey, SingleArrow, WhiteCloseButton } = IMAGE_PATH;

/**
 * SearchableDropdownComponent - A dropdown component that contains an optional search field to filter dropdown results
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const SearchableDropdownComponent = props => {
  const { cssClass, dropdownId, dropdownList, dropdownTitleText, enableSearch, onDropdownSelection, selectedText } =
    props;

  const [filteredDropdownList, setFilteredDropdownList] = useState(dropdownList);
  const [dropdownListVisible, setDropdownListVisible] = useState(false);
  const [clearButtonVisible, setClearButtonVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropDownRef = useRef();
  const inputRef = useCallback(inputElement => {
    if (inputElement) {
      inputElement.focus();
    }
  }, []);

  /**
   * handleOutsideClick - When the user clicks outside the dropdown container,
   * the event listener's callback function checks
   * if the event target is inside the dropdown container.
   * */
  useEffect(() => {
    const handleOutsideClick = event => {
      if (dropDownRef.current && !dropDownRef.current.contains(event.target)) {
        setDropdownListVisible(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  /**
   * getFilteredDropdownSelections - takes current search query and filters out none matching values from dropdownList
   * @param newSearchQuery {String} - currently entered search string
   */
  const getFilteredDropdownSelections = newSearchQuery => {
    const searchPattern = newSearchQuery.toLowerCase();
    let filteredDropdownSuggestions = dropdownList;
    if (searchPattern) {
      filteredDropdownSuggestions = dropdownList?.filter(item => item.toLowerCase().indexOf(searchPattern) !== -1);
    }
    setFilteredDropdownList(filteredDropdownSuggestions);
  };

  /**
   * onChangeSearchText - updates query every time a new value is entered in the search field
   * @param event
   */
  const onChangeSearchText = event => {
    event?.preventDefault();
    event?.stopPropagation();
    const newSearchQuery = event?.target?.value || '';
    setSearchQuery(newSearchQuery);
    getFilteredDropdownSelections(newSearchQuery);
  };

  /**
   * onKeyDown - checks for specific key presses that require escaping search focus
   * @param event
   */
  const onKeyDown = event => {
    if (event.key === 'Enter' || event.keyCode === 13) {
      event?.preventDefault();
      event?.stopPropagation();
      event?.target?.blur();
    } else if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
      event?.preventDefault();
      event?.stopPropagation();
      showDropdownListPicker();
    }
  };

  /**
   * onClearTextPressed - resets the search field and query if the clear text icon is pressed
   * @param event
   */
  const onClearTextPressed = event => {
    event?.preventDefault();
    event?.stopPropagation();
    setSearchQuery('');
    setFilteredDropdownList(dropdownList);
  };

  /**
   * onPressDropdownListItem - event to handle when item in list is pressed to select it
   * @param event
   * @param item {String}
   */
  const onPressDropdownListItem = (event, item) => {
    event?.preventDefault();
    event?.stopPropagation();
    setDropdownListVisible(!dropdownListVisible);
    onDropdownSelection(item);
    onClearTextPressed();
  };

  /**
   * onDisplayClearButton - determines whether the clear button icon should be visible
   * @param displayClearButton {Boolean}
   */
  const onDisplayClearButton = displayClearButton => {
    setClearButtonVisible(displayClearButton);
  };

  /**
   * showDropdownListPicker - checks if the filteredDropdownList has values and sets bool make the list visible
   */
  const showDropdownListPicker = () => {
    setDropdownListVisible(!dropdownListVisible);
    onClearTextPressed();
  };

  return (
    <div className={`searchable-dropdown ${cssClass}`} ref={dropDownRef}>
      {dropdownTitleText && (
        <div className='searchable-dropdown-title' aria-level='1' role='heading'>
          {selectedText !== dropdownTitleText || dropdownListVisible ? dropdownTitleText : ''}
        </div>
      )}
      <div
        id={dropdownId}
        className='searchable-dropdown-value'
        data-testid='searchable-dropdown-value-id'
        role='listbox'
        tabIndex='0'
        onClick={showDropdownListPicker}
      >
        <div className='selected-value'>
          {selectedText !== dropdownTitleText || !dropdownListVisible ? selectedText : ''}
        </div>
        <img src={SingleArrow} alt=' ' className={`down-arrow-img ${dropdownListVisible ? 'dropdown-open' : ''}`} />
      </div>
      {dropdownListVisible && (
        <div className='searchable-dropdown-options-container'>
          {enableSearch && (
            <div className='searchable-dropdown-form-container'>
              <form className='searchable-dropdown-form'>
                <img src={SearchIconGrey} alt=' ' className='search-icon' />
                <input
                  className='searchable-dropdown-input'
                  data-testid='searchable-dropdown-search-input'
                  type='text'
                  ref={inputRef}
                  placeholder={translateWithI18Next(DELIVERY_ADDRESS_SEARCH_FORM_CONSTANTS.SEARCH)}
                  value={searchQuery}
                  onChange={event => onChangeSearchText(event)}
                  onKeyDown={event => onKeyDown(event)}
                  onFocus={() => onDisplayClearButton(true)}
                  onBlur={() => onDisplayClearButton(false)}
                />
                {(clearButtonVisible || searchQuery) && (
                  <button
                    className='clear-text'
                    data-testid='searchable-dropdown-clear-text'
                    onClick={event => onClearTextPressed(event)}
                  >
                    <img src={WhiteCloseButton} alt=' ' className='search-icon' />
                  </button>
                )}
              </form>
            </div>
          )}
          <ul>
            {filteredDropdownList?.map(item => (
              <li
                key={item}
                tabIndex='0'
                onClick={event => onPressDropdownListItem(event, item)}
                className='searchable-dropdown-option'
                data-testid='searchable-dropdown-option-id'
              >
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

SearchableDropdownComponent.defaultProps = {
  cssClass: '',
  dropdownId: '',
  dropdownList: [],
  dropdownTitleText: '',
  enableSearch: false,
  onDropdownSelection: () => null,
  selectedText: '',
};

SearchableDropdownComponent.propTypes = {
  cssClass: PropTypes.string,
  dropdownId: PropTypes.string,
  dropdownList: PropTypes.array,
  dropdownTitleText: PropTypes.string,
  enableSearch: PropTypes.bool,
  onDropdownSelection: PropTypes.func,
  selectedText: PropTypes.string,
};

export default SearchableDropdownComponent;
